import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SEOHead } from '@/components/SEOHead';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Search, Building2, Home, MapPin, TrendingUp, Users, Shield, ArrowRight,
  Landmark, Calculator, BarChart3, Newspaper,
  ChevronLeft, ChevronRight, Star, Phone, ExternalLink,
  LucideIcon, LandPlot, Briefcase, Image as ImageIcon, FileText, Award, Map,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import heroBg from '@/assets/hero-bg.jpg';
import homeAdvisorAiIcon from '@/assets/home-advisor-ai.png';
import avmIcon from '@/assets/avm.png';
import newProjectsIcon from '@/assets/new-projects.png';
import societyMapsIcon from '@/assets/society-maps.png';
import areaGuidesIcon from '@/assets/area-guides.png';
import costCalculatorIcon from '@/assets/cost-calculator.png';
import ethioPropertyInvestIcon from '@/assets/ethio-property-invest.png';
import { optimizedImageUrl, optimizedSrcSet } from '@/lib/imageOptimizer';
import {
  useHero, useCities, useExploreTiles, useLatestNews, useLatestBlog, useLocalize,
} from '@/hooks/useLandingContent';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice, formatPriceRange } from '@/lib/formatPrice';
import { CurrencySwitcher } from '@/components/CurrencySwitcher';
import { StoriesRow } from '@/components/landing/StoriesRow';
import { FavoritePropertiesRow } from '@/components/landing/FavoritePropertiesRow';
import { ViewedProjectsRow } from '@/components/landing/ViewedProjectsRow';
import { ViewedPropertiesRow } from '@/components/landing/ViewedPropertiesRow';
import { TitaniumAgencies } from '@/components/landing/TitaniumAgencies';
import { TrendingProjectsTwoRow } from '@/components/landing/TrendingProjectsTwoRow';
import { CommunitySection } from '@/components/landing/CommunitySection';
import { PartnersSection } from '@/components/landing/PartnersSection';
import { PropertyScrollRow } from '@/components/landing/PropertyScrollRow';
import { NewPropertiesRow } from '@/components/landing/NewPropertiesRow';
import { MobileBrowsePropertiesCard } from '@/components/landing/MobileBrowsePropertiesCard';
import { HeroPropertyTypePopover, HeroCategory } from '@/components/landing/HeroPropertyTypePopover';
import { MobileNewsScroll } from '@/components/landing/MobileNewsScroll';
import { MobilePostAdCTA } from '@/components/landing/MobilePostAdCTA';
import { MobileAgenciesRow } from '@/components/landing/MobileAgenciesRow';
import { MobileTitaniumAgencies } from '@/components/landing/MobileTitaniumAgencies';
import { useIsMobile } from '@/hooks/use-mobile';
import { BannerSlot } from '@/components/banners/BannerSlot';

// Map icon names stored in `explore_tiles.icon_name` to Lucide components
const ICON_MAP: Record<string, LucideIcon> = {
  Landmark, Calculator, BarChart3, MapPin, Map, Building2, Home, TrendingUp, Users,
  Shield, LandPlot, Briefcase, FileText, Award, Newspaper, Image: ImageIcon,
};

export default function Index() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const localize = useLocalize();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'buy' | 'rent' | 'projects'>('buy');
  const [selectedCity, setSelectedCity] = useState('Addis Ababa');
  const [heroCategory, setHeroCategory] = useState<HeroCategory>('homes');
  const [heroSubtype, setHeroSubtype] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minArea, setMinArea] = useState('');
  const [maxArea, setMaxArea] = useState('');
  const [beds, setBeds] = useState('all');
  const agencyScrollRef = useRef<HTMLDivElement>(null);
  const projectScrollRef = useRef<HTMLDivElement>(null);
  const { displayCurrency, rate } = useCurrency();
  const isMobile = useIsMobile();

  // CMS-driven content
  const { data: hero } = useHero();
  const { data: citiesData } = useCities();
  const { data: exploreTiles } = useExploreTiles();
  const { data: latestNews } = useLatestNews(3);
  const { data: blogPostsData } = useLatestBlog(3);

  const cityNames = (citiesData && citiesData.length > 0)
    ? citiesData.map(c => c.name)
    : ['Addis Ababa'];

  // Keep selected city valid as the list loads
  useEffect(() => {
    if (cityNames.length > 0 && !cityNames.includes(selectedCity)) {
      setSelectedCity(cityNames[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citiesData]);


  // Dynamic data queries
  const { data: approvedProjects } = useQuery({
    queryKey: ['landing-projects'],
    queryFn: async () => {
      const { data } = await supabase
        .from('projects')
        .select('*, project_images(url, is_primary), developer_profiles(company_name, phone, whatsapp)')
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false })
        .limit(8);
      return data || [];
    },
  });

  const { data: verifiedAgencies } = useQuery({
    queryKey: ['landing-agencies'],
    queryFn: async () => {
      const { data } = await supabase
        .from('agencies')
        .select('*')
        .eq('is_verified', true)
        .limit(10);
      return data || [];
    },
  });

  const { data: newProperties } = useQuery({
    queryKey: ['landing-new-properties'],
    queryFn: async () => {
      // Include both freshly created listings AND boosted/refreshed ones
      // (featured first, then most recently updated).
      const { data } = await supabase
        .from('properties')
        .select('*, property_images(url, is_primary)')
        .eq('approval_status', 'approved')
        .eq('status', 'active')
        .order('is_featured', { ascending: false })
        .order('updated_at', { ascending: false })
        .limit(12);
      return data || [];
    },
  });

  const { data: stats } = useQuery({
    queryKey: ['landing-stats'],
    queryFn: async () => {
      const [propRes, profileRes, agencyRes] = await Promise.all([
        supabase.from('properties').select('id', { count: 'exact', head: true }).eq('approval_status', 'approved').eq('status', 'active'),
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('agencies').select('id', { count: 'exact', head: true }).eq('is_verified', true),
      ]);
      return {
        properties: propRes.count || 0,
        users: profileRes.count || 0,
        agencies: agencyRes.count || 0,
      };
    },
  });

  const handleSearch = () => {
    if (activeTab === 'projects') {
      navigate(`/projects?q=${encodeURIComponent(searchQuery)}`);
      return;
    }
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCity) params.set('city', selectedCity);
    if (heroSubtype) params.set('type', heroSubtype);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (minArea) params.set('minArea', minArea);
    if (maxArea) params.set('maxArea', maxArea);
    if (heroCategory === 'homes' && beds !== 'all') params.set('beds', beds);

    let basePath: string;
    if (heroCategory === 'plots') basePath = '/plots';
    else if (heroCategory === 'commercial') basePath = '/commercial';
    else basePath = `/${activeTab}`;
    navigate(`${basePath}?${params.toString()}`);
  };

  const scroll = (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
    if (ref.current) {
      ref.current.scrollBy({ left: direction === 'left' ? -300 : 300, behavior: 'smooth' });
    }
  };

  const getName = (val: any) => (typeof val === 'object' ? val?.en || '' : val || '');

  const statItems = [
    { icon: Building2, value: stats ? `${stats.properties.toLocaleString()}+` : '...', label: 'Properties Listed' },
    { icon: Users, value: stats ? `${stats.users.toLocaleString()}+` : '...', label: 'Registered Users' },
    { icon: MapPin, value: citiesData ? `${citiesData.length}+` : '...', label: 'Cities Covered' },
    { icon: TrendingUp, value: stats ? `${stats.agencies}+` : '...', label: 'Verified Agencies' },
  ];

  // Fallback agencies if none verified yet (kept minimal until DB populated)
  const fallbackAgencies: any[] = [];

  const fallbackProjects: any[] = [];

  const displayAgencies = verifiedAgencies?.length ? verifiedAgencies : fallbackAgencies;
  const displayProjects = approvedProjects?.length ? approvedProjects : fallbackProjects;

  const homepageJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Ethio Real Estates",
      url: "https://ethio-real-estate-hub.lovable.app",
      logo: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/7f169d66-3955-46a0-86f4-b65736a568fd",
      description: "Ethiopia's leading real estate platform for buying, renting, and investing in property.",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      url: "https://ethio-real-estate-hub.lovable.app",
      name: "Ethio Real Estates",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://ethio-real-estate-hub.lovable.app/buy?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Ethio Real Estates — Buy, Rent & Invest in Ethiopian Property"
        description="Ethiopia's leading real estate platform. Search thousands of properties for sale and rent in Addis Ababa, Adama, Hawassa and across Ethiopia. Find houses, apartments, villas, land and new projects."
        canonical="/"
        jsonLd={homepageJsonLd}
      />
      {/* Hero Section */}
      <section className="relative min-h-[260px] sm:min-h-[420px] md:min-h-[480px] flex items-center justify-center bg-primary sm:bg-transparent">
        <img
          src={heroBg}
          alt="Addis Ababa cityscape"
          className="absolute inset-0 w-full h-full object-cover hidden sm:block"
          width={1920}
          height={800}
          loading="eager"
          decoding="async"
          // @ts-expect-error - fetchpriority is a valid HTML attribute, React 18 types lag behind
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60 hidden sm:block" />
        <div className="relative z-10 container py-8 md:py-24 text-center">
          <h1 className="font-display text-base sm:text-2xl md:text-[44px] font-bold leading-tight text-white mb-3 md:mb-6 px-2 line-clamp-2 md:line-clamp-none">
            {hero ? localize(hero.title) : 'Search properties for sale in Ethiopia'}
          </h1>
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-center gap-1 mb-0">
              {(['buy', 'rent', 'projects'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 sm:px-8 py-2 sm:py-2.5 rounded-t-lg text-xs sm:text-sm font-semibold transition-all ${
                    activeTab === tab ? 'bg-white text-foreground' : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                  }`}
                >
                  {tab === 'buy' ? 'BUY' : tab === 'rent' ? 'RENT' : 'PROJECTS'}
                </button>
              ))}
            </div>
            <div className="bg-white rounded-b-lg rounded-tr-lg p-3 md:p-4 shadow-2xl">
              <div className="flex flex-col sm:flex-row gap-2">
                {activeTab !== 'projects' && (
                  <div className="hidden sm:block sm:w-[180px]">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1 block pl-1">City</label>
                    <Select value={selectedCity} onValueChange={setSelectedCity}>
                      <SelectTrigger aria-label="Select city" className="h-10 border-border/60 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{cityNames.map(city => <SelectItem key={city} value={city}>{city}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex-1">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1 block pl-1">
                    {activeTab === 'projects' ? 'Search Projects' : 'Location'}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={activeTab === 'projects' ? 'Search by project name or developer...' : 'Search by area, neighborhood...'}
                      value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSearch()} className="pl-10 h-10 border-border/60 text-sm"
                    />
                  </div>
                </div>
                <div className="sm:w-auto flex items-end">
                  <Button onClick={handleSearch} className="w-full sm:w-auto h-10 px-8 font-bold text-sm">FIND</Button>
                </div>
              </div>

              {activeTab !== 'projects' && (
                <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
                  <div>
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1 block pl-1">Property Type</label>
                    <HeroPropertyTypePopover
                      category={heroCategory}
                      setCategory={setHeroCategory}
                      subtype={heroSubtype}
                      setSubtype={setHeroSubtype}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1 block pl-1">Price ({displayCurrency})</label>
                    <div className="flex items-center gap-1 h-10 px-2 rounded-md border border-border/60 bg-background">
                      <Input type="number" placeholder="0" value={minPrice} onChange={e => setMinPrice(e.target.value)} className="h-8 border-0 px-1 text-xs focus-visible:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                      <span className="text-xs text-muted-foreground">to</span>
                      <Input type="number" placeholder="Any" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} className="h-8 border-0 px-1 text-xs focus-visible:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1 block pl-1">Area (Sq. M.)</label>
                    <div className="flex items-center gap-1 h-10 px-2 rounded-md border border-border/60 bg-background">
                      <Input type="number" placeholder="0" value={minArea} onChange={e => setMinArea(e.target.value)} className="h-8 border-0 px-1 text-xs focus-visible:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                      <span className="text-xs text-muted-foreground">to</span>
                      <Input type="number" placeholder="Any" value={maxArea} onChange={e => setMaxArea(e.target.value)} className="h-8 border-0 px-1 text-xs focus-visible:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                    </div>
                  </div>
                  {heroCategory === 'homes' && (
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1 block pl-1">Beds</label>
                      <Select value={beds} onValueChange={setBeds}>
                        <SelectTrigger className="h-10 border-border/60 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {['1','2','3','4','5','6'].map(n => <SelectItem key={n} value={n}>{n === '6' ? '6+' : n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              <div className="hidden sm:flex items-center gap-2 sm:gap-3 mt-2 pt-2 border-t border-border/30 flex-wrap">
                <CurrencySwitcher variant="hero" />
                <span className="text-border hidden sm:inline">|</span>
                <button
                  className="text-[10px] sm:text-xs text-primary hover:underline"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCity(cityNames[0] || 'Addis Ababa');
                    setHeroSubtype('');
                    setMinPrice(''); setMaxPrice('');
                    setMinArea(''); setMaxArea('');
                    setBeds('all');
                  }}
                >Reset Search</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {isMobile ? (
        <>
          {/* Mobile: streamlined Zameen-style layout in our brand colors */}
          <MobileBrowsePropertiesCard />
          <div className="px-3 pt-4">
            <BannerSlot placementCode="homepage_top_mobile" page="homepage" />
          </div>
          <StoriesRow />
          <ViewedPropertiesRow />
          <MobilePostAdCTA />
          <NewPropertiesRow properties={(newProperties || []).slice(0, 12)} title="New Properties" />
          <div className="px-3 py-3">
            <BannerSlot placementCode="homepage_middle_mobile" page="homepage" />
          </div>
          <MobileTitaniumAgencies />
          <TrendingProjectsTwoRow projects={displayProjects.slice(0, 8)} />
          <MobileNewsScroll />
        </>
      ) : (
        <>
          {/* Stories */}
          <StoriesRow />

          {/* Browse Properties */}
          <section className="container py-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl md:text-2xl font-bold">Browse Properties</h2>
              <Button variant="link" className="text-primary text-sm font-semibold p-0" onClick={() => navigate('/buy')}>
                View all <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
            {newProperties && newProperties.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {newProperties.map((property: any) => {
                  const title = typeof property.title === 'object' ? property.title?.en || '' : property.title || '';
                  const primaryImg = property.property_images?.find((img: any) => img.is_primary)?.url || property.property_images?.[0]?.url;
                  const imgSrc = optimizedImageUrl(primaryImg || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop', { width: 400, height: 300, quality: 75 });
                  const imgSrcSet = optimizedSrcSet(primaryImg || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop', { width: 400, height: 300, quality: 75 });
                  const currency = property.currency || 'ETB';
                  const formattedPrice = formatPrice(property.price, currency, displayCurrency, rate);
                  return (
                    <div
                      key={property.id}
                      className="rounded-xl border bg-card overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                      onClick={() => navigate(`/property/${property.id}`)}
                    >
                      <div className="relative aspect-[4/3]">
                        <img src={imgSrc} srcSet={imgSrcSet} alt={title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded uppercase">
                          {property.listing_type === 'rent' ? 'RENT' : 'SALE'}
                        </span>
                        {property.is_featured && (
                          <span className="absolute top-2 right-2 px-2 py-0.5 bg-destructive text-destructive-foreground text-[10px] font-bold rounded uppercase">HOT</span>
                        )}
                      </div>
                      <div className="p-3 space-y-1">
                        <div className="font-bold text-primary text-sm">{formattedPrice}</div>
                        <div className="font-semibold text-sm leading-tight truncate">{title}</div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 shrink-0" />
                          {property.city}{property.sub_city ? `, ${property.sub_city}` : ''}
                        </div>
                        <div className="flex items-center gap-3 pt-1 text-xs text-muted-foreground">
                          {property.bedrooms != null && <span>{property.bedrooms} Beds</span>}
                          {property.bathrooms != null && <span>{property.bathrooms} Baths</span>}
                          {property.area_sqm != null && <span>{property.area_sqm} sqm</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">No new properties yet.</div>
            )}
          </section>

          {/* Explore More */}
          <section className="container py-8">
            <h2 className="font-display text-xl md:text-2xl font-bold mb-6">Explore More on EthioRealEstates</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {(exploreTiles || []).map(tile => {
                const Icon = ICON_MAP[tile.icon_name] || Landmark;
                const customIcon: Record<string, { src: string; alt: string }> = {
                  HomeAdvisorAI: { src: homeAdvisorAiIcon, alt: 'HomeAdvisor AI' },
                  AVM: { src: avmIcon, alt: 'Automated Valuation Models' },
                  NewProjects: { src: newProjectsIcon, alt: 'New Projects' },
                  SocietyMaps: { src: societyMapsIcon, alt: 'Society Maps' },
                  AreaGuides: { src: areaGuidesIcon, alt: 'Area Guides' },
                  CostCalculator: { src: costCalculatorIcon, alt: 'Cost Calculator' },
                  EthioPropertyInvest: { src: ethioPropertyInvestIcon, alt: 'Ethio Property Invest' },
                };
                const custom = customIcon[tile.icon_name];
                return (
                  <button key={tile.id} onClick={() => navigate(tile.link_url)}
                    className="group flex items-start gap-2.5 sm:gap-3.5 p-3 sm:p-4 rounded-xl hover:shadow-md transition-all text-left">
                    <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl ${tile.background_class || 'bg-amber-50'} flex items-center justify-center shrink-0 overflow-hidden`}>
                      {custom ? (
                        <img src={custom.src} alt={custom.alt} className="h-full w-full object-contain" />
                      ) : (
                        <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${tile.color_class || 'text-amber-600'}`} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-xs sm:text-sm leading-tight">{localize(tile.title)}</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-snug line-clamp-2">{localize(tile.description)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <FavoritePropertiesRow />
          <ViewedPropertiesRow />
          <ViewedProjectsRow />
          <TitaniumAgencies />
          <TrendingProjectsTwoRow projects={displayProjects} />
          <CommunitySection />
          <PartnersSection />
        </>
      )}

    </Layout>
  );
}
